import { gsap } from 'gsap'
import { getSceneImagePath } from '../../../utils/pathResolver'
import { SaveStateManager } from '../../../features/save-state'
import { getAudioManager } from '../../../features/audio'
import { showGlobalScrollIndicator, hideGlobalScrollIndicator } from '../../../utils/scroll-indicator'
import type { SceneData } from '../../scenes/types'
import {
  SCENE_ID,
  BUBBLE_DISPLAY_MS,
  WRONG_BUBBLE_COUNT,
  LEAF_COUNT,
  CLEAN_SOUND_COUNT,
  STOPY_ORDER,
  ZVIRATA_ORDER,
  STOPY_POSITIONS,
  ZVIRATA_POSITIONS,
  CARY_POSITIONS,
  CORRECT_BUBBLE_NAMES,
  ANIMAL_FILE_STEM,
  buildLeaves,
  type AnimalId,
} from './constants'
import './PrirazovaniStop.css'

interface PrirazovaniStopState {
  clearedLeaves: number[]
  matchedPairs: string[]
  completed: boolean
}

function loadState(): PrirazovaniStopState {
  const saved = SaveStateManager.load()
  const mg = saved?.minigames?.[SCENE_ID] as Partial<PrirazovaniStopState> | undefined
  return {
    clearedLeaves: mg?.clearedLeaves ?? [],
    matchedPairs: mg?.matchedPairs ?? [],
    completed: mg?.completed ?? false,
  }
}

function saveState(state: PrirazovaniStopState): void {
  const saved = SaveStateManager.load() ?? SaveStateManager.createInitialState()
  if (!saved.minigames) saved.minigames = {}
  saved.minigames[SCENE_ID] = {
    clearedLeaves: [...state.clearedLeaves],
    matchedPairs: [...state.matchedPairs],
    completed: state.completed,
  }
  SaveStateManager.saveImmediate(saved)
}

/**
 * `matchedPairs` is saved on the last correct tap before the celebratory bubble dismisses
 * and `onAllMatched` runs. If the player leaves in that window, restore `completed` from pairs.
 */
function reconcileCompletedFromMatches(state: PrirazovaniStopState): void {
  const matched = new Set(state.matchedPairs)
  const allMatched = ZVIRATA_ORDER.every((id) => matched.has(id))
  if (allMatched && !state.completed) {
    state.completed = true
    saveState(state)
  }
}

function isElementInViewport(el: HTMLElement): boolean {
  const rect = el.getBoundingClientRect()
  return rect.top < window.innerHeight && rect.bottom > 0
}

function findNextSceneWrapper(root: HTMLElement, onFound: (el: HTMLElement) => void): void {
  let attempts = 0
  function check() {
    attempts++
    const sceneWrapper = root.closest('.scene-wrapper')
    if (sceneWrapper) {
      let next = sceneWrapper.nextElementSibling as HTMLElement | null
      while (next && !next.classList.contains('scene-wrapper')) {
        next = next.nextElementSibling as HTMLElement | null
      }
      if (next) {
        onFound(next)
        return
      }
    }
    if (attempts < 20) setTimeout(check, 100)
  }
  check()
}

export function createMinigame(container: HTMLElement, onComplete?: () => void, _sceneData?: SceneData): HTMLElement {
  const root = document.createElement('div')
  root.className = 'minigame-prirazovani-stop'
  root.setAttribute('data-minigame', SCENE_ID)

  const state = loadState()
  reconcileCompletedFromMatches(state)
  const clearedSet = new Set(state.clearedLeaves)
  const matchedSet = new Set(state.matchedPairs)
  const leaves = buildLeaves()

  let selectedStopa: AnimalId | null = null
  let selectedZvire: AnimalId | null = null
  let wrongBubbleIdx = 0
  let activeBubbleTimeout: ReturnType<typeof setTimeout> | null = null
  let activeBubbleEl: HTMLElement | null = null
  let cleanSoundIdx = 0
  let scrollTarget: HTMLElement | null = null
  let phase1Done = clearedSet.size >= LEAF_COUNT
  let phase2Active = state.completed || phase1Done

  // ── Leaf counter panel ──
  const counterPanel = document.createElement('div')
  counterPanel.className = 'minigame-prirazovani-stop__leaf-counter'
  if (phase1Done) counterPanel.classList.add('minigame-prirazovani-stop__leaf-counter--hidden')

  const counterIcon = document.createElement('img')
  counterIcon.src = getSceneImagePath(SCENE_ID, 'icon-list.png')
  counterIcon.alt = ''
  counterIcon.className = 'minigame-prirazovani-stop__leaf-counter-icon'
  counterPanel.appendChild(counterIcon)

  const counterText = document.createElement('span')
  counterPanel.appendChild(counterText)
  root.appendChild(counterPanel)

  function updateLeafCounter() {
    const remaining = LEAF_COUNT - clearedSet.size
    counterText.textContent = String(remaining)
  }
  updateLeafCounter()

  // ── Scene area (height driven by aspect-ratio, no bg image) ──
  const sceneArea = document.createElement('div')
  sceneArea.className = 'minigame-prirazovani-stop__scene-area'

  const itemsLayer = document.createElement('div')
  itemsLayer.className = 'minigame-prirazovani-stop__items-layer'
  sceneArea.appendChild(itemsLayer)
  root.appendChild(sceneArea)

  // ── Cary (connecting lines) ──
  const caraEls: Record<string, HTMLDivElement> = {}
  ZVIRATA_ORDER.forEach((animalId) => {
    const pos = CARY_POSITIONS[animalId]
    const wrapper = document.createElement('div')
    wrapper.className = 'minigame-prirazovani-stop__cara'
    wrapper.style.left = `${pos.left}%`
    wrapper.style.top = `${pos.top}%`
    wrapper.style.width = `${pos.width}%`
    wrapper.style.height = `${pos.height}%`
    if (matchedSet.has(animalId)) wrapper.classList.add('minigame-prirazovani-stop__cara--visible')
    const img = document.createElement('img')
    img.src = getSceneImagePath(SCENE_ID, `cary/cara-${ANIMAL_FILE_STEM[animalId]}.png`)
    img.alt = ''
    wrapper.appendChild(img)
    caraEls[animalId] = wrapper
    itemsLayer.appendChild(wrapper)
  })

  // ── Stopy buttons ──
  const stopaButtons: Record<string, HTMLButtonElement> = {}
  STOPY_ORDER.forEach((animalId) => {
    const pos = STOPY_POSITIONS[animalId]
    const btn = document.createElement('button')
    btn.className = 'minigame-prirazovani-stop__stopa'
    btn.type = 'button'
    btn.setAttribute('data-animal', animalId)
    btn.style.left = `${pos.left}%`
    btn.style.top = `${pos.top}%`
    btn.style.width = `${pos.width}%`

    if (matchedSet.has(animalId)) {
      btn.classList.add('minigame-prirazovani-stop__stopa--matched')
      btn.disabled = true
    } else if (!phase2Active) {
      btn.classList.add('minigame-prirazovani-stop__stopa--grayscale')
      btn.disabled = true
    }

    const img = document.createElement('img')
    img.src = getSceneImagePath(SCENE_ID, `stopy/stopa-${ANIMAL_FILE_STEM[animalId]}.png`)
    img.alt = animalId
    btn.appendChild(img)

    btn.addEventListener('click', () => onStopaClick(animalId))
    stopaButtons[animalId] = btn
    itemsLayer.appendChild(btn)
  })

  // ── Zvirata buttons ──
  const zvireButtons: Record<string, HTMLButtonElement> = {}
  ZVIRATA_ORDER.forEach((animalId) => {
    const pos = ZVIRATA_POSITIONS[animalId]
    const btn = document.createElement('button')
    btn.className = 'minigame-prirazovani-stop__zvire'
    btn.type = 'button'
    btn.setAttribute('data-animal', animalId)
    btn.style.left = `${pos.left}%`
    btn.style.top = `${pos.top}%`
    btn.style.width = `${pos.width}%`

    if (matchedSet.has(animalId)) {
      btn.classList.add('minigame-prirazovani-stop__zvire--matched')
      btn.disabled = true
    } else if (!phase2Active) {
      btn.disabled = true
      btn.classList.add('minigame-prirazovani-stop__zvire--grayscale')
    }

    const img = document.createElement('img')
    img.src = getSceneImagePath(SCENE_ID, `zvirata/zvire-${ANIMAL_FILE_STEM[animalId]}.png`)
    img.alt = animalId
    btn.appendChild(img)

    btn.addEventListener('click', () => onZvireClick(animalId))
    zvireButtons[animalId] = btn
    itemsLayer.appendChild(btn)
  })

  // ── Leaves ──
  const leafButtons: HTMLButtonElement[] = []
  leaves.forEach((leaf, idx) => {
    if (clearedSet.has(idx)) return

    const btn = document.createElement('button')
    btn.className = 'minigame-prirazovani-stop__leaf'
    btn.type = 'button'
    btn.setAttribute('data-leaf-idx', String(idx))
    btn.style.left = `${leaf.left}%`
    btn.style.top = `${leaf.top}%`
    btn.style.width = `${leaf.width}%`
    btn.style.transform = `rotate(${leaf.rotation}deg)`

    const img = document.createElement('img')
    img.src = getSceneImagePath(SCENE_ID, `listy/list-${leaf.variant}.png`)
    img.alt = ''
    btn.appendChild(img)

    btn.addEventListener('click', () => onLeafClick(btn, idx))
    leafButtons.push(btn)
    itemsLayer.appendChild(btn)
  })

  // ── Scroll indicator ──
  function onScroll() {
    if (!scrollTarget) return
    if (isElementInViewport(scrollTarget)) {
      hideGlobalScrollIndicator()
      scrollTarget = null
    }
  }
  window.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('resize', onScroll, { passive: true })
  ;(root as unknown as Record<string, () => void>).__minigameCleanup = () => {
    window.removeEventListener('scroll', onScroll)
    window.removeEventListener('resize', onScroll)
    hideGlobalScrollIndicator()
  }

  // ── Persistent bubble-2 (shown after phase 1, stays in DOM) ──
  function appendPermanentBubble2(animate: boolean) {
    const existing = root.querySelector('.minigame-prirazovani-stop__bubble-permanent')
    if (existing) return

    const bubble = document.createElement('button')
    bubble.className = 'minigame-prirazovani-stop__bubble-permanent'
    bubble.type = 'button'
    const img = document.createElement('img')
    img.src = getSceneImagePath(SCENE_ID, 'bubliny/bubble-2.png')
    img.alt = ''
    bubble.appendChild(img)

    bubble.addEventListener('mouseenter', () => {
      gsap.to(bubble, { scale: 0.95, duration: 0.2, ease: 'power2.out', overwrite: 'auto' })
    })
    bubble.addEventListener('mouseleave', () => {
      gsap.to(bubble, { scale: 1, duration: 0.2, ease: 'power2.out', overwrite: 'auto' })
    })
    bubble.addEventListener('click', () => {
      getAudioManager().tryPlayCustomSound('bubliny/bubble-2.mp3', SCENE_ID, 'speech')
      gsap.to(bubble, { scale: 0.8, duration: 0.05, ease: 'power2.out', overwrite: 'auto' })
      gsap.to(bubble, { scale: 1, duration: 0.12, delay: 0.05, ease: 'back.out(1.5)', overwrite: 'auto' })
    })

    root.insertBefore(bubble, sceneArea)

    if (animate) {
      gsap.fromTo(bubble, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' })
      getAudioManager().tryPlayCustomSound('bubliny/bubble-2.mp3', SCENE_ID, 'speech')
    }
  }

  // If phase 1 is already done (state restore), show permanent bubble-2 immediately
  if (phase1Done) {
    appendPermanentBubble2(false)
  }

  // ── Already completed — load next scene immediately ──
  if (state.completed) {
    phase2Active = true
    if (onComplete) onComplete()
  }

  // ── Leaf click handler ──
  function onLeafClick(btn: HTMLButtonElement, idx: number) {
    if (btn.disabled) return
    btn.disabled = true

    clearedSet.add(idx)
    state.clearedLeaves.push(idx)
    saveState(state)
    updateLeafCounter()

    const audio = getAudioManager()
    const soundNum = (cleanSoundIdx % CLEAN_SOUND_COUNT) + 1
    cleanSoundIdx++
    audio.tryPlayCustomSound(`clean-${soundNum}.mp3`, SCENE_ID)

    const dirX = (Math.random() - 0.5) * 200
    const dirY = -(30 + Math.random() * 80)
    gsap.to(btn, {
      x: dirX,
      y: dirY,
      rotation: `+=${(Math.random() - 0.5) * 120}`,
      opacity: 0,
      duration: 0.5,
      ease: 'power2.out',
      onComplete: () => btn.remove(),
    })

    if (clearedSet.size >= LEAF_COUNT && !phase1Done) {
      phase1Done = true
      counterPanel.classList.add('minigame-prirazovani-stop__leaf-counter--hidden')
      setTimeout(() => onPhase1Complete(), 600)
    }
  }

  // ── Phase 1 complete ──
  function onPhase1Complete() {
    showBubble('bubble-1', BUBBLE_DISPLAY_MS, () => {
      appendPermanentBubble2(true)

      const permBubble = root.querySelector('.minigame-prirazovani-stop__bubble-permanent')
      const scrollEl = permBubble ?? sceneArea
      const lenis = (window as any).__lenis as
        | { scrollTo: (target: Element | number, opts?: { duration?: number; offset?: number }) => void }
        | undefined
      if (lenis?.scrollTo) {
        lenis.scrollTo(scrollEl, { duration: 1.2, offset: -40 })
      } else {
        scrollEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }

      setTimeout(() => activatePhase2(), 1400)
    })
  }

  function activatePhase2() {
    phase2Active = true
    STOPY_ORDER.forEach((id) => {
      const btn = stopaButtons[id]
      if (!matchedSet.has(id)) {
        btn.disabled = false
        btn.classList.remove('minigame-prirazovani-stop__stopa--grayscale')
      }
    })
    ZVIRATA_ORDER.forEach((id) => {
      const btn = zvireButtons[id]
      if (!matchedSet.has(id)) {
        btn.disabled = false
        btn.classList.remove('minigame-prirazovani-stop__zvire--grayscale')
      }
    })
  }

  function clearSelection() {
    if (selectedStopa) {
      stopaButtons[selectedStopa].classList.remove('minigame-prirazovani-stop__stopa--selected')
      selectedStopa = null
    }
    if (selectedZvire) {
      zvireButtons[selectedZvire].classList.remove('minigame-prirazovani-stop__zvire--selected')
      selectedZvire = null
    }
    root.classList.remove('minigame-prirazovani-stop--selection-active')
  }

  // ── Stopa click ──
  function onStopaClick(animalId: AnimalId) {
    const btn = stopaButtons[animalId]
    if (btn.disabled || matchedSet.has(animalId)) return

    if (selectedZvire) {
      if (selectedZvire === animalId) {
        onCorrectMatch(animalId)
      } else {
        onWrongMatch()
      }
      return
    }

    if (selectedStopa === animalId) {
      clearSelection()
      return
    }

    clearSelection()
    selectedStopa = animalId
    btn.classList.add('minigame-prirazovani-stop__stopa--selected')
    root.classList.add('minigame-prirazovani-stop--selection-active')
  }

  // ── Zvire click ──
  function onZvireClick(animalId: AnimalId) {
    const btn = zvireButtons[animalId]
    if (btn.disabled || matchedSet.has(animalId)) return

    if (selectedStopa) {
      if (selectedStopa === animalId) {
        onCorrectMatch(animalId)
      } else {
        onWrongMatch()
      }
      return
    }

    if (selectedZvire === animalId) {
      clearSelection()
      return
    }

    clearSelection()
    selectedZvire = animalId
    btn.classList.add('minigame-prirazovani-stop__zvire--selected')
    root.classList.add('minigame-prirazovani-stop--selection-active')
  }

  // ── Correct match ──
  function onCorrectMatch(animalId: AnimalId) {
    matchedSet.add(animalId)
    state.matchedPairs.push(animalId)
    saveState(state)

    clearSelection()

    const stopaBtn = stopaButtons[animalId]
    const zvireBtn = zvireButtons[animalId]

    stopaBtn.classList.add('minigame-prirazovani-stop__stopa--matched')
    stopaBtn.disabled = true
    zvireBtn.classList.add('minigame-prirazovani-stop__zvire--matched')
    zvireBtn.disabled = true

    const cara = caraEls[animalId]
    if (cara) {
      cara.classList.add('minigame-prirazovani-stop__cara--visible')
      gsap.fromTo(cara, { opacity: 0 }, { opacity: 1, duration: 0.4, ease: 'power2.out' })
    }

    const bubbleName = CORRECT_BUBBLE_NAMES[animalId]
    if (matchedSet.size >= ZVIRATA_ORDER.length) {
      showBubble(bubbleName, BUBBLE_DISPLAY_MS, () => onAllMatched())
    } else {
      showBubble(bubbleName, BUBBLE_DISPLAY_MS)
    }
  }

  // ── Wrong match ──
  function onWrongMatch() {
    clearSelection()
    wrongBubbleIdx = (wrongBubbleIdx % WRONG_BUBBLE_COUNT) + 1
    showBubble(`bubble-wrong-${wrongBubbleIdx}`, BUBBLE_DISPLAY_MS)
  }

  // ── All matched — THIS is when onComplete fires ──
  function onAllMatched() {
    state.completed = true
    saveState(state)
    showBubble('bubble-3', BUBBLE_DISPLAY_MS, () => {
      if (onComplete) onComplete()
      showGlobalScrollIndicator()
      findNextSceneWrapper(root, (el) => {
        scrollTarget = el
      })
    })
  }

  // ── Show bubble ──
  function showBubble(name: string, fallbackMs: number, onDone?: () => void) {
    if (activeBubbleTimeout) {
      clearTimeout(activeBubbleTimeout)
      activeBubbleTimeout = null
    }
    if (activeBubbleEl?.parentNode) {
      activeBubbleEl.parentNode.removeChild(activeBubbleEl)
    }

    const bubble = document.createElement('div')
    bubble.className = 'minigame-prirazovani-stop__bubble'
    bubble.style.left = '10%'
    bubble.style.width = '80%'
    bubble.style.top = '35%'

    const img = document.createElement('img')
    img.src = getSceneImagePath(SCENE_ID, `bubliny/${name}.png`)
    img.alt = ''
    bubble.appendChild(img)

    gsap.set(bubble, { scale: 0, transformOrigin: 'center center' })
    itemsLayer.appendChild(bubble)
    gsap.to(bubble, { scale: 1, duration: 0.4, ease: 'back.out(1.7)' })

    activeBubbleEl = bubble

    const scheduleDismiss = (ms: number) => {
      activeBubbleTimeout = setTimeout(() => {
        gsap.to(bubble, {
          scale: 0,
          duration: 0.3,
          ease: 'back.in(1.7)',
          onComplete: () => {
            if (bubble.parentNode) bubble.parentNode.removeChild(bubble)
            if (activeBubbleEl === bubble) activeBubbleEl = null
            onDone?.()
          },
        })
        activeBubbleTimeout = null
      }, ms)
    }

    getAudioManager().tryPlayCustomSound(`bubliny/${name}.mp3`, SCENE_ID, 'speech').then((durationMs) => {
      if (activeBubbleEl !== bubble) return
      scheduleDismiss(durationMs > 0 ? durationMs + 500 : fallbackMs)
    })
  }

  return root
}
